import React, { useContext, useMemo } from 'react'

import CruiseSearchTile from './CruiseSearchTile'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import { offerHasValidDepartures, getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import moment from 'moment'
import { sortBy } from 'lib/array/arrayUtils'
import FrontPageTileFlightOnlyPricing from 'components/OfferList/OfferListTiles/Support/FrontPageTileFlightOnlyPricing'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import TextButton from 'components/Luxkit/Button/TextButton'
import GeoContext from 'contexts/geoContext'
import styled from 'styled-components'
import FrontPageTileFlightPricing from 'components/OfferList/OfferListTiles/Support/FrontPageTileFlightPricing'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferTileLoggedInButtons from 'components/OfferList/OfferListTiles/Support/OfferTileLoggedInButtons'
import { rem } from 'polished'
import { PrimaryButtonPalette } from 'contexts/PrimaryButtonPaletteProvider'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import { useIsTabletAndSmallerScreen } from 'lib/web/deviceUtils'
import useQueryParams from 'hooks/useQueryParams'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

interface Props {
  offer: App.CruiseOffer | App.Offer | App.OfferSummary;
}

interface CruiseBankProps {
  offer: App.CruiseOffer;
}
interface FlashCruiseProps {
  offer: App.Offer | App.OfferSummary;
}

const StyledFrontPageTileFlightPricing = styled(FrontPageTileFlightPricing)`
  grid-column: 1 / -1;

  ${mediaQueryUp.desktop} {
    justify-content: center;
  }
`
const FlashPrice = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: end;
  gap: ${rem(8)};
  ${mediaQueryUp.tablet} {
    flex-direction: column;
    align-items: start;
  }
`

function CruiseBankOffer(props: CruiseBankProps) {
  const { offer } = props
  const { departurePort, returnPort, itinerary, mainDepartureDetails } = offer
  const { lowestOverallPriceDetails, lowestPriceDetailsByCategory } = mainDepartureDetails
  const { promotion, discountPills } = lowestOverallPriceDetails
  const promotionDetails = promotion ?? offer.fallbackPromotion

  const hasDepartures = offerHasValidDepartures(offer)
  if (!hasDepartures) {
    throw new Error('no departures')
  }

  const cities = itinerary?.map((event) => {
    const { port } = event
    if (!port) return
    return port.city || port.name.split(',')[0]
  }).filter((city) => city && city.length > 1)

  const countries = itinerary?.map((itineraryItem) => {
    const { port } = itineraryItem
    if (!port) return
    return port.countryCode
  }).filter(country => country?.length)

  const placesCount = [...new Set(cities)].length
  const countriesCount = [...new Set(countries)].length

  const {
    primaryPrice,
    secondaryPrice,
  } = getCruiseOfferPriceVariation(offer, lowestOverallPriceDetails)

  const hasTheSameStartAndEndPort = departurePort === returnPort

  const departureDates = sortBy(
    offer.departureDates?.map((date) => moment(date)) ?? [],
    (date) => date.unix(),
    'asc',
  )

  const params = useQueryParams()

  const selectedDepartureDate = useMemo(() => {
    const months = params.get('departureMonths')?.split(',') ?? []

    if (months.length) {
      return departureDates.find(date => months.includes(date.format('YYYY-MM')))?.format(DMY_CASUAL_FORMAT)
    }
    return departureDates[0].format(DMY_CASUAL_FORMAT)
  }, [departureDates, params])

  const departureDatesList = useMemo(() => {
    const formattedDepartureDates = departureDates.map(date => date.format(DMY_CASUAL_FORMAT))

    if (mainDepartureDetails.departureDate) {
      const mainDepartureDate = moment(mainDepartureDetails.departureDate).format(DMY_CASUAL_FORMAT)
      return [...new Set([mainDepartureDate, ...formattedDepartureDates])]
    }
    return formattedDepartureDates
  },
  [departureDates, mainDepartureDetails.departureDate])

  const [earliestDepartureDate, ...laterDates] = departureDatesList

  const returnPortCity = returnPort?.split(',')?.[0]
  const departurePortCity = departurePort?.split(',')?.[0]
  const shipInfoDescription = offer.ship.shipInfo?.find(info => info.dataTypeName === 'Summary')?.description
  const cruiseDescription = offer?.description || shipInfoDescription || offer.destinationDescription

  const parsedDescription = cruiseDescription
    ?.replace(/(<([^>]+)>)/gi, '')
    ?.split(' ')
    ?.slice(0, 35)
    ?.join(' ') + '...'

  return (
    <CruiseSearchTile
      offer={offer}
      parsedDescription={parsedDescription}
      bookmarkButton={<BookmarkButton offer={offer} />}
      returnPortCity={returnPortCity}
      departurePortCity={departurePortCity}
      hasTheSameStartAndEndPort={hasTheSameStartAndEndPort}
      lowestOverallPriceDetails={lowestOverallPriceDetails}
      cities={cities}
      countriesCount={countriesCount}
      placesCount={placesCount}
      discountPills={discountPills}
      promotionDetails={promotionDetails}
      secondaryPrice={secondaryPrice}
      primaryPrice={primaryPrice}
      laterDates={laterDates}
      earliestDepartureDate={selectedDepartureDate || earliestDepartureDate}
      lowestPriceDetailsByCategory={lowestPriceDetailsByCategory}
      shipName={offer.ship.name}
      paletteValue="default"
      {...offer}
  />)
}

function FlashCruiseOffer(props: FlashCruiseProps) {
  const { currentRegionHasFlights } = useContext(GeoContext)
  const { offer } = props
  const { lowestPricePackage, offerFlightsEnabled } = offer
  const tour = lowestPricePackage?.tour
  const isNight = offer.tileDurationLabel?.toLowerCase().includes('night') ? 0 : 1
  const duration = lowestPricePackage && lowestPricePackage?.duration - isNight
  const isMobileScreen = useIsTabletAndSmallerScreen()

  const pricingSection = useMemo(() => (<FlashPrice>
    {offer.bundledWithFlightsOnly && <FrontPageTileFlightOnlyPricing offer={offer} />}
    {!offer.bundledWithFlightsOnly && lowestPricePackage &&
      <OfferTilePricing
        offer={offer}
        pkg={lowestPricePackage}
        offerUrl=""
      />}

    {isMobileScreen && <TextButton kind="primary" fit="auto">View</TextButton>}
    {!isMobileScreen && <TextButton kind="primary" fit="flex">View offer</TextButton>}
    {currentRegionHasFlights && offerFlightsEnabled && !offer.bundledWithFlightsOnly &&
      <StyledFrontPageTileFlightPricing offer={offer}/>
    }
  </FlashPrice>), [offer, lowestPricePackage, offerFlightsEnabled, currentRegionHasFlights, isMobileScreen])

  const inclusions = offer.tileInclusions?.map((inclusion) => inclusion.text)
  const travelToDate = moment(offer.travelToDate).format('MMM YYYY')
  const travelFromDate = offer.travelFromDate && moment(offer.travelFromDate).format('MMM YYYY')

  let departureDates
  if (offer.travelFromDate && (offer.travelToDate !== offer.travelFromDate)) {
    departureDates = [
      offer.travelToDate && moment(offer.travelToDate).format('MMM YYYY'),
    ].filter(date => date !== undefined)
  }
  const isUltraLux = offer.property?.isUltraLux || offer.productType === 'ultra_lux_tour'

  let paletteValue: PrimaryButtonPalette
  if (isUltraLux) {
    paletteValue = 'ultralux'
  } else {
    paletteValue = 'default'
  }
  const description = offer.preHeader

  return (
    <CruiseSearchTile
      flashOffer
      inclusions={inclusions}
      parsedDescription={description}
      pricingSection={<>
        {!offer.walledGarden && pricingSection}

        {offer.walledGarden && <OfferTileLoggedInButtons
                  signUpText="Sign up for free"
                  discountPercentage={Math.round(lowestPricePackage?.discountPercent ?? 0)}
                  align="none"
                  fullWidth
                >
          {pricingSection}
        </OfferTileLoggedInButtons>}
      </>}
      bookmarkButton={<BookmarkButton offer={offer} />}
      cruiseLine={{ name: offer.vendorName as string, id: offer.vendorName as string, imageId: offer.property?.logoImageId || tour?.logoImageId }}
      images={offer.images}
      name={offer.name}
      shipName={offer.vendorVehicle}
      productType={offer.productType}
      returnPortCity={offer.endLocation}
      departurePortCity={offer.startLocation}
      hasTheSameStartAndEndPort={offer.endLocation === offer.startLocation}
      duration={duration}
      cities={offer.locations}
      placesCount={offer.locations?.length}
      earliestDepartureDate={travelFromDate || travelToDate}
      laterDates={departureDates}
      paletteValue={paletteValue}
    />
  )
}

function CruiseSearchTileStandard(props: Props) {
  const { offer } = props

  if (isCruiseOffer(offer)) {
    return <CruiseBankOffer offer={offer}/>
  }

  return <FlashCruiseOffer offer={offer} />
}

export default CruiseSearchTileStandard
